// ========================================
// UTILITÁRIOS DE SEGURANÇA - FRONTEND
// src/utils/security.js
// ========================================

/**
 * Auth agora é via cookies HttpOnly + CSRF.
 * Este arquivo mantém sanitização/validações de input.
 * As rotinas antigas de token/localStorage foram desativadas para evitar regressões.
 */

import { textoTemConteudoBloqueado } from './conteudoBloqueado';

const TOKEN_KEY = 'token';
const USER_KEY = 'usuario';
const TOKEN_EXPIRY_KEY = 'token_expiry';

// ========================================
// FUNÇÕES DE TOKEN
// ========================================

/**
 * Salva o token de forma segura
 */
export const saveToken = (token) => {
    try {
        // Compatibilidade: não persistir mais token no storage
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
        return true;
    } catch {
        return true;
    }
};

/**
 * Recupera o token validando se ainda é válido
 */
export const getToken = () => {
    return null;
};

/**
 * Verifica se o token expirou
 */
export const isTokenExpired = () => {
    return true;
};

/**
 * Retorna tempo restante do token em segundos
 */
export const getTokenTimeRemaining = () => {
    return 0;
};

// ========================================
// FUNÇÕES DE USUÁRIO
// ========================================

/**
 * Salva dados do usuário
 */
export const saveUser = (user) => {
    try {
        // Compatibilidade: não persistir mais usuário no storage
        localStorage.removeItem(USER_KEY);
        return true;
    } catch {
        return true;
    }
};

/**
 * Recupera dados do usuário
 */
export const getUser = () => {
    return null;
};

// ========================================
// FUNÇÕES DE LIMPEZA
// ========================================

/**
 * Limpa todos os dados de autenticação
 */
export const clearAuthData = () => {
    try {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
    } catch (error) {
        console.error('Erro ao limpar dados de autenticação:', error);
    }
};

// ========================================
// VALIDAÇÃO E SANITIZAÇÃO
// ========================================

/**
 * Sanitiza string para prevenir XSS
 */
export const sanitizeString = (str) => {
    if (typeof str !== 'string') return str;
    
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;')
        .replace(/\//g, '&#x2F;');
};

/**
 * Sanitiza objeto recursivamente
 */
export const sanitizeObject = (obj) => {
    if (obj === null || obj === undefined) return obj;
    
    if (Array.isArray(obj)) {
        return obj.map(item => sanitizeObject(item));
    }
    
    if (typeof obj === 'object') {
        const sanitized = {};
        for (const key of Object.keys(obj)) {
            sanitized[key] = sanitizeObject(obj[key]);
        }
        return sanitized;
    }
    
    if (typeof obj === 'string') {
        return sanitizeString(obj);
    }
    
    return obj;
};

/**
 * Valida email
 */
export const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

/**
 * Valida CNPJ (formato básico)
 */
export const isValidCNPJ = (cnpj) => {
    const cleanCnpj = cnpj.replace(/\D/g, '');
    return cleanCnpj.length === 14;
};

/**
 * Valida CPF (formato básico)
 */
export const isValidCPF = (cpf) => {
    const cleanCpf = cpf.replace(/\D/g, '');
    return cleanCpf.length === 11;
};

// ========================================
// VERIFICAÇÃO DE AUTENTICAÇÃO
// ========================================

/**
 * Verifica se o usuário está autenticado
 */
export const isAuthenticated = () => {
    const token = getToken();
    const user = getUser();
    return !!token && !!user && !isTokenExpired();
};

/**
 * Obtém headers de autenticação para requisições
 */
export const getAuthHeaders = () => {
    const token = getToken();
    
    if (!token) {
        return {
            'Content-Type': 'application/json'
        };
    }

    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
    };
};

// ========================================
// PROTEÇÃO CONTRA ATAQUES
// ========================================

/**
 * Gera um nonce para proteção CSRF
 */
export const generateNonce = () => {
    const array = new Uint8Array(16);
    window.crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Detecta conteúdo não permitido em uma string.
 *
 * As regras ficam em utils/conteudoBloqueado.js, iguais às do backend, para a
 * mensagem de erro ser a mesma nos dois lados. A lista antiga que existia aqui
 * tinha /data:/i e /on\w+=/i, que barravam texto legítimo como "Data: 10/09" e
 * "Desconto = 5%" antes mesmo da requisição sair do navegador.
 */
export const containsXSS = (str) => textoTemConteudoBloqueado(str);

export default {
    saveToken,
    getToken,
    isTokenExpired,
    getTokenTimeRemaining,
    saveUser,
    getUser,
    clearAuthData,
    sanitizeString,
    sanitizeObject,
    isValidEmail,
    isValidCNPJ,
    isValidCPF,
    isAuthenticated,
    getAuthHeaders,
    generateNonce,
    containsXSS
};
